import Button from "@components/UI/Button";
import clsx from "clsx";
import styles from "./styles.module.sass";
import { useStore, useStoreMap } from "effector-react";
import useAuthedUserCourse from "@hooks/useAuthedUserCourse";
import { useRouter } from "next/router";
import { useEvent } from "effector-react/scope";
import { useWebAnalyticsContext } from "@shared/analytics/webAnalytics.contexts";
import Loader from "../../../../../shared/UI/Loader";
import {
	$favouriteCourses,
	toggleFavouriteCourse,
	toggleFavouriteCourseFx,
} from "@features/FavouriteCourses/model/public";
import { $course } from "@features/Course/model/public";
import { $isAuth } from "../../../../../process/authProcess/model/public";
import { useShoppingCart } from "@features/ShoppingChart/hooks/useShoppingCart";
import { ROUTES } from "../../../../../constants/ROUTES";
import { $shoppingCart } from "@features/ShoppingChart/model/public";
import { getFavouriteCoursesFx } from "@models/users/getFavouriteCourses";
import { roundNumber } from "@shared/functions/roundNumber";

type CourseActionsProps = {
	isPreviewMode: boolean;
};

const CourseActions = ({ isPreviewMode }: CourseActionsProps) => {
	const isAuth = useStore($isAuth);
	const course = useStore($course);
	const isFavCoursesLoading = useStore(getFavouriteCoursesFx.pending);
	const isToggleFavLoading = useStore(toggleFavouriteCourseFx.pending);
	const { handleAddToCart } = useShoppingCart({
		isAuth,
	});

	const toggleFavouriteCourseEvent = useEvent(toggleFavouriteCourse);

	const router = useRouter();
	const { reachGoal, addProductToCart } = useWebAnalyticsContext();
	const { isCourseOwner, isPurchased } = useAuthedUserCourse();

	const isFavorite = useStoreMap($favouriteCourses, (favoriteCourses) => {
		const foundFavoriteCourse = favoriteCourses?.items?.find((favoriteCourse) => {
			return favoriteCourse.id === course?.id;
		});

		return foundFavoriteCourse !== undefined;
	});

	const isAddedToCart = useStoreMap($shoppingCart, (cart) => {
		if (cart !== null) {
			const foundAddedCourse = cart.courses.find((courseInCart) => {
				return courseInCart.id === course?.id;
			});

			return foundAddedCourse !== undefined;
		}
		return false;
	});

	const isFree = course?.isFree;

	const handleAddToCartClick = async () => {
		if (!course) {
			return;
		}

		if (isPurchased && isAuth) {
			return router.push(`/course/${course.url}/watch/`);
		}

		if (!isFree) {
			reachGoal("click-order");
		}
		if (isFree) {
			reachGoal("free-click-order");
		}
		addProductToCart({
			category: course.category?.title || "",
			id: course.id.toString(),
			discount: course.discount,
			price: roundNumber(course.cost),
			name: course.title,
		});
		handleAddToCart(course.id);

		await router.push(ROUTES.CART);
	};

	const handleFavClick = () => {
		if (!course) {
			return;
		}

		toggleFavouriteCourseEvent({
			id: course.id,
		});
	};

	const getButtonText = () => {
		if (isAddedToCart && !isFree) {
			return "Создание заказа";
		}
		if (isFree) {
			return "Получить знание";
		}
		if (isPurchased && isAuth) {
			return "Перейти к курсу";
		}

		return "Оформить заказ";
	};

	const buttonText = getButtonText();

	const buttonTooltip = isCourseOwner ? "Вы являетесь автором этого курса" : undefined;

	return (
		<div className={styles.actions}>
			<div className={styles.addToCartButtonWrapper} title={buttonTooltip}>
				<Button
					text={buttonText}
					upperCase
					disabled={isPreviewMode || (isAddedToCart && !isFree) || isCourseOwner}
					onClick={handleAddToCartClick}
				/>
			</div>
			{isAuth && (
				<button
					title="Добавить в избранное"
					disabled={isPreviewMode}
					className={clsx(styles.favButton, {
						[styles.disabled]: isPreviewMode,
						[styles.checked]: isFavorite,
					})}
					onClick={handleFavClick}
				>
					{isFavCoursesLoading || isToggleFavLoading ? <Loader /> : <div className={styles.favIcon} />}
				</button>
			)}
		</div>
	);
};

export default CourseActions;
